// @givmo/board — one person's answers for a year, as the officer reads them (CBG-YR2-WEB1; frames
// o6-answers, o7-changes), at `/conflicts/annual/$cycleId/responses/$recordId` beneath the Conflicts of
// interest page (`?view=changes` for the changes from their previous year).
//
// The page shows the RECORD's own words — every question as it was asked, the answer given, how it was
// signed or recorded, its fingerprint — and, for a year after the first, the backend's comparison with
// the answers it replaced, marked in words and by underline and strike-through. Both downloads are the
// backend's PDFs of the same reads. Nothing here says what an answer means.

import { ArrowLeft, ChevronDown, Download } from 'lucide-react';
import { Button, Card, CardBody, Icon, Menu, Skeleton, Tabs, toast } from '@givmo/design-system';
import { CONFLICTS_TABS, BOARD_PAGES } from '../../board-routes';
import { Link, useNavigate, useParams, useSearch } from '../../board-routing';
import { useBoardHost } from '../../board-host';
import { useBoardOperations } from '../../board-runtime';
import { PageShell } from '../../components/page-shell';
import { displayDate } from '../../lib/display';
import { fillQuestion } from './coi-decode';
import { CleanCopy, DocumentCard, Redline, SignatureBlock, TwoFingerprints } from './coi-document';
import { pdfName, readerTimeZone, savePdf } from './coi-download';
import { useCoiChanges, useCoiCycle, useCoiResponse, useCoiTemplate } from './coi-queries';
import { coiWords as w } from './coi-words';

const SECONDARY = 'font-sans text-s14 font-w400 tracking-givmo text-text-secondary';
const BACK =
  'inline-flex items-center gap-6 self-start font-sans text-s13 font-w600 tracking-givmo text-brand-600 outline-none hover:text-brand-700 focus-visible:underline';

export function CoiResponsePage() {
  const { cycleId, recordId } = useParams({
    from: '/conflicts/annual/$cycleId/responses/$recordId',
  });
  const search = useSearch();
  const navigate = useNavigate();
  const operations = useBoardOperations();
  const { orgRef, orgName } = useBoardHost();
  // The organization's name in the short titles where the host has none (approved, CBG-YR2-COPY1).
  const organization = orgName?.trim() || 'the organization';
  const view = search.view === 'changes' ? 'changes' : 'answers';

  const responseQuery = useCoiResponse(orgRef, cycleId, recordId);
  const response = responseQuery.data ?? null;
  const hasPrevious = response?.previous !== null && response?.previous !== undefined;
  const changesQuery = useCoiChanges(orgRef, cycleId, recordId, hasPrevious);
  const cycleQuery = useCoiCycle(orgRef, cycleId);
  const templateQuery = useCoiTemplate(orgRef, cycleQuery.data?.questionnaire.template_id ?? null);
  const previousLabel = response?.previous?.cycle_label ?? null;

  const shortTitleOf = (questionId: string): string | null => {
    const question = templateQuery.data?.questions.find((candidate) => candidate.id === questionId);
    return question ? fillQuestion(question.short_title, organization, '') : null;
  };

  const go = (next: 'answers' | 'changes') =>
    void navigate({
      to: BOARD_PAGES.coiResponse,
      params: { cycleId, recordId },
      ...(next === 'changes' ? { search: { view: 'changes' } } : {}),
      replace: true,
    });

  const download = async (which: 'clean' | 'changes') => {
    try {
      const { data } = await operations.officerGetBoardCoiResponseDocument({
        path: { org_ref: orgRef ?? '', cycle_id: cycleId, record_id: recordId },
        query: { view: which, time_zone: readerTimeZone() },
        throwOnError: true,
      });
      savePdf(
        data,
        pdfName(
          response?.respondent.name,
          response?.cycle_label,
          which === 'changes' ? 'changes' : 'conflicts of interest form',
        ),
      );
    } catch {
      toast.error(w.couldNotDownload);
    }
  };

  return (
    <PageShell
      title="Conflicts of interest"
      description={w.pageDescription}
      testId="page-coi-response"
    >
      <Tabs.Root
        appearance="saas"
        value={CONFLICTS_TABS.annual}
        onValueChange={(next) =>
          void navigate({
            to: BOARD_PAGES.conflicts,
            search: {
              tab:
                next === CONFLICTS_TABS.register ? CONFLICTS_TABS.register : CONFLICTS_TABS.annual,
            },
          })
        }
        testId="conflicts-tabs"
      >
        <Tabs.List aria-label="Conflicts of interest">
          <Tabs.Tab value={CONFLICTS_TABS.register} testId="conflicts-tab-register">
            {w.registerTab}
          </Tabs.Tab>
          <Tabs.Tab value={CONFLICTS_TABS.annual} testId="conflicts-tab-annual">
            {w.annualTab}
          </Tabs.Tab>
        </Tabs.List>
      </Tabs.Root>

      <div className="flex flex-col gap-16" data-testid="coi-response">
        <Link
          to={BOARD_PAGES.conflicts}
          search={{ tab: CONFLICTS_TABS.annual }}
          // Exact: this page sits beneath that address, and it is not the current page.
          exact
          className={BACK}
          data-testid="coi-response-back"
        >
          <Icon icon={ArrowLeft} size={14} aria-hidden />
          {w.backToYear(response?.cycle_label ?? cycleQuery.data?.label ?? '')}
        </Link>

        {responseQuery.isPending ? (
          <div className="flex flex-col gap-12" data-testid="coi-response-loading">
            <Skeleton className="h-48 w-full rounded-lg" />
            <Skeleton className="h-84 w-full rounded-lg" />
          </div>
        ) : response === null ? (
          <Card testId="coi-response-error">
            <CardBody className="flex flex-col items-start gap-12">
              <p className={SECONDARY}>{w.yearUnavailable}</p>
              <Button
                appearance="saas"
                tone="secondary"
                onClick={() => void responseQuery.refetch()}
              >
                {w.tryAgain}
              </Button>
            </CardBody>
          </Card>
        ) : (
          <>
            <div className="flex flex-wrap items-start justify-between gap-12">
              <div className="flex min-w-0 flex-col gap-4">
                <h2 className="font-sans text-s20 font-w700 tracking-givmo text-text-strong">
                  {response.respondent.name}
                </h2>
                <p className={SECONDARY} data-testid="coi-response-facts">
                  {w.responseFacts(
                    response.respondent.capacity,
                    response.cycle_label,
                    response.period_label,
                  )}
                </p>
              </div>
              <Menu.Root>
                <Menu.Trigger
                  render={
                    <Button
                      appearance="saas"
                      tone="secondary"
                      leadingIcon={<Icon icon={Download} size={16} aria-hidden />}
                      trailingIcon={<Icon icon={ChevronDown} size={15} aria-hidden />}
                      testId="coi-response-download"
                    />
                  }
                >
                  {w.download}
                </Menu.Trigger>
                <Menu.Content saas align="end" className="min-w-[240px]">
                  <Menu.Item
                    saas
                    onClick={() => void download('clean')}
                    testId="coi-response-download-clean"
                  >
                    <span className="flex flex-col gap-2">
                      <span>{w.theseAnswers}</span>
                      <span className="text-s12 font-w400 text-text-tertiary">
                        {response.capture.road === 'paper'
                          ? w.theseAnswersPaperNote
                          : w.theseAnswersNote}
                      </span>
                    </span>
                  </Menu.Item>
                  {previousLabel !== null ? (
                    <Menu.Item
                      saas
                      onClick={() => void download('changes')}
                      testId="coi-response-download-changes"
                    >
                      <span className="flex flex-col gap-2">
                        <span>{w.changesPdf(previousLabel)}</span>
                        <span className="text-s12 font-w400 text-text-tertiary">
                          {w.changesPdfNote}
                        </span>
                      </span>
                    </Menu.Item>
                  ) : null}
                </Menu.Content>
              </Menu.Root>
            </div>

            {previousLabel !== null ? (
              <Tabs.Root
                appearance="saas"
                value={view}
                onValueChange={(next) => go(next === 'changes' ? 'changes' : 'answers')}
                testId="coi-response-tabs"
              >
                <Tabs.List aria-label={response.respondent.name ?? ''}>
                  <Tabs.Tab value="answers" testId="coi-response-tab-answers">
                    {w.answersTab}
                  </Tabs.Tab>
                  <Tabs.Tab value="changes" testId="coi-response-tab-changes">
                    {w.changesTab(previousLabel)}
                  </Tabs.Tab>
                </Tabs.List>
              </Tabs.Root>
            ) : null}

            {response.corrected_by_record_id ? (
              <Card testId="coi-response-corrected">
                <CardBody className="flex flex-wrap items-center justify-between gap-8">
                  <span className={SECONDARY}>{w.correctedLater}</span>
                  <Link
                    to={BOARD_PAGES.coiResponse}
                    params={{ cycleId, recordId: response.corrected_by_record_id }}
                    className="font-sans text-s14 font-w600 tracking-givmo text-brand-600 hover:text-brand-700"
                    data-testid="coi-response-see-correction"
                  >
                    {w.seeCorrection}
                  </Link>
                </CardBody>
              </Card>
            ) : null}

            {view === 'changes' && previousLabel !== null ? (
              changesQuery.data ? (
                <DocumentCard
                  testId="coi-redline"
                  header={
                    <p className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-strong">
                      {w.changesHeader(
                        response.respondent.name ?? '',
                        previousLabel,
                        displayDate(changesQuery.data.previous?.occurred_on),
                        response.cycle_label,
                        displayDate(changesQuery.data.current.occurred_on),
                      )}
                    </p>
                  }
                  note={w.changesNote}
                >
                  <Redline
                    changes={changesQuery.data}
                    previousLabel={previousLabel}
                    shortTitleOf={shortTitleOf}
                    testId="coi-redline-body"
                  />
                  <TwoFingerprints changes={changesQuery.data} />
                </DocumentCard>
              ) : (
                <Skeleton className="h-84 w-full rounded-lg" />
              )
            ) : (
              <DocumentCard
                testId="coi-clean"
                header={
                  <>
                    {response.corrects_record_id ? (
                      <p className={SECONDARY}>{w.correctsEarlier}</p>
                    ) : null}
                    <SignatureBlock response={response} testId="coi-clean-signature" />
                  </>
                }
                note={w.inMinuteBookNote}
              >
                <CleanCopy response={response} testId="coi-clean-body" />
              </DocumentCard>
            )}
          </>
        )}
      </div>
    </PageShell>
  );
}
